/* eslint-disable react/no-is-mounted */
// Copyright (c) Microsoft. All rights reserved.

import { URLSearchParams } from 'url';
import Cookies, { CookieSetOptions } from 'universal-cookie';
import { IApiResult } from '../models/app/APIResult';

interface ServiceRequest {
  commandPath: string;
  method?: string;
  body?: unknown;
  query?: URLSearchParams;
  headers?: Record<string, string>;
}

const noResponseBodyStatusCodes = [202, 204];

export const BackendServiceUrl =
  process.env.REACT_APP_BACKEND_URI == null || process.env.REACT_APP_BACKEND_URI.trim() === ''
    ? window.origin
    : process.env.REACT_APP_BACKEND_URI;
export const NetworkErrorMessage = '\n\nPlease check that your backend is running and that it is accessible by the app';

export let accessToken: string | null = null;

export class BaseService {
  constructor(protected readonly serviceUrl: string = BackendServiceUrl) {}

  protected readonly setAccessToken = (token: string) => {
    accessToken = token;
    this.setCookie('AI_TOKEN', token);
  };

  protected readonly getAccessToken = () => {
    if(window.electron?.isElectron){
      const electron_cookie = document.cookie.split('; ')
      .find(row => row.startsWith('AI_TOKEN='))
      ?.split('=')[1];

      if(electron_cookie) return electron_cookie ;
    }

    const cookies = new Cookies();
    const cookie = cookies.get('AI_TOKEN') as string | undefined;
    
    //2025.07.01 AI_TOKEN에 undefined로 저장된 채로 오류 나는 버그 조치
    if(cookie === 'undefined') {
      cookies.remove('AI_TOKEN');
      return undefined;
      //throw new Error(`오류가 발생하였습니다. \n키보드 Ctrl+Shift+Del를 한번에 누르고 '쿠키 및 기타 사이트 데이터'(*지난 7일) 삭제 후 재접속해주세요.`);
    }else return cookie;

  };

  protected readonly setCookie = (key: string, value: string) => {
    const cookies = new Cookies();
    //const domain = window.location.hostname.replace('www','').split('.').reverse().splice(0,3).reverse().join('.');
    const domain = window.location.hostname;
    const option: CookieSetOptions = { path: '/', domain: domain, sameSite: 'none', secure: true };
    cookies.set(key, value, option);
  };

  protected readonly getResponseAsync = async <T>(request: ServiceRequest): Promise<T> => {
    const { commandPath, method, body, query } = request;
    const isFormData = body instanceof FormData;
    // const headers = new Headers(
    //   accessToken
    //     ? {
    //         Authorization: `Bearer ${this.getAccessToken()}`,
    //       }
    //     : undefined,
    // );
     const headers = new Headers(
      accessToken
        ? {
            Authorization: `Bearer ${accessToken}`,
          }
        : {
            Authorization: `Bearer ${this.getAccessToken()}`,
          }
    );

    if (!isFormData) {
      headers.append('Content-Type', 'application/json');
      console.log('isFormData not getResponseAsync ');
    }

    try {
      const requestUrl = new URL(commandPath, this.serviceUrl);
      if (query) {
        requestUrl.search = `?${query.toString()}`;
      }

      const response = await fetch(requestUrl, {
        method: method ?? 'GET',
        body: isFormData ? body : JSON.stringify(body),
        headers,
      });

      if (!response.ok) {
        if (response.status === 504) {
          throw Object.assign(new Error('The request timed out. Please try sending your message again.'));
        }

        const responseText = await response.text();
        const isJsonResponse = response.headers.get('content-type')?.includes('application/json');
        const responseDetailsJSON = isJsonResponse
          ? (JSON.parse(responseText) as IApiResult)
          : { status: response.statusText, message: response.body };
        const responseDetails = responseText.split('--->');

        const errorDetails =
          responseDetails.length > 1
            ? `${responseDetails[0].trim()} ---> ${responseDetails[1].trim()}`
            : responseDetails[0];
        const errorMessage = `${response.status}: ${response.statusText}${errorDetails}`;

        class CustomError extends Error {
          status: string;

          constructor(message: string, status: string) {
            super(message);
            this.status = status;
          }
        }

        throw new CustomError(errorMessage, responseDetailsJSON.status);
      }

      const contentType = response.headers.get('content-type');
      // 응답이 JSON인지 체크
      if (contentType?.includes('application/json')) {
        return (noResponseBodyStatusCodes.includes(response.status) ? {} : await response.json()) as T;
      } else if (contentType?.includes('text/plain')) {
        // API가 URL을 텍스트로 반환한다고 가정
        const url = await response.text();
        return url as unknown as T; // TypeScript 오류를 피하기 위해 unknown으로 변환
      }
      throw new Error('예상치 못한 응답 형식입니다.');
    } catch (e) {
      let isNetworkError = false;
      if (e instanceof TypeError) {
        // fetch() will reject with a TypeError when a network error is encountered.
        isNetworkError = true;
      }
      if ((e as IApiResult).status) {
        throw e;
      }
      throw Object.assign(new Error(`${e as string} ${isNetworkError ? NetworkErrorMessage : ''}`));
    }
  };
}
